import { TileType } from "./TileMap";
import { ThemeConfig } from "./ThemeConfig";

/**
 * 装飾の種類
 */
export type DecorationType = "torch" | "crystal";

/**
 * 結晶1つのジオメトリ（タイル内ローカル座標、0〜1正規化）
 */
export interface CrystalGem {
  /** タイル内Y位置（0〜1） */
  top: number;
  /** タイル内X位置（0〜1） */
  left: number;
  /** 幅（px） */
  width: number;
  /** 高さ（px） */
  height: number;
  /** 回転角度（度） */
  rotation: number;
}

/**
 * 装飾の配置情報
 */
export interface DecorationPlacement {
  type: DecorationType;
  x: number;
  y: number;
  /** 結晶の場合、個々のジオメトリ */
  gems?: CrystalGem[];
}

/**
 * たいまつ周辺の暖色ライティング情報
 */
export type LightLevel = "center" | "lit";

/**
 * mulberry32: シード付き擬似乱数生成器
 * 同じシードで同じ結果を返す（リスタート時に配置が変わらない）
 */
function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 配列をシード付きでシャッフル（Fisher-Yates）
 */
function seededShuffle<T>(arr: T[], rng: () => number): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * マップ内のすべての壁タイルを返す
 */
export function findAllWalls(
  tiles: readonly (readonly TileType[])[],
  mapSize: number
): { x: number; y: number }[] {
  const result: { x: number; y: number }[] = [];
  for (let y = 0; y < mapSize; y++) {
    for (let x = 0; x < mapSize; x++) {
      if (tiles[y][x] === "wall") {
        result.push({ x, y });
      }
    }
  }
  return result;
}

/**
 * 通路に面した壁タイルを検出
 */
export function findWallsAdjacentToFloor(
  tiles: readonly (readonly TileType[])[],
  mapSize: number
): { x: number; y: number }[] {
  const result: { x: number; y: number }[] = [];

  for (let y = 0; y < mapSize; y++) {
    for (let x = 0; x < mapSize; x++) {
      if (tiles[y][x] !== "wall") continue;

      // 上下左右のいずれかが床なら候補
      const neighbors = [
        { nx: x - 1, ny: y },
        { nx: x + 1, ny: y },
        { nx: x, ny: y - 1 },
        { nx: x, ny: y + 1 },
      ];

      const adjacentToFloor = neighbors.some(
        ({ nx, ny }) =>
          nx >= 0 &&
          nx < mapSize &&
          ny >= 0 &&
          ny < mapSize &&
          tiles[ny][nx] === "floor"
      );

      if (adjacentToFloor) {
        result.push({ x, y });
      }
    }
  }

  return result;
}

/**
 * 装飾（たいまつ＋結晶）の配置を計算する純粋関数
 *
 * Phaserに依存しないため、テストが容易。
 *
 * @param tiles タイルマップ（2D配列）
 * @param mapSize マップサイズ（正方形）
 * @param config テーマ設定
 * @param seed シード値（ステージIDなど。同じ値で同じ配置）
 */
export function computeDecorations(
  tiles: readonly (readonly TileType[])[],
  mapSize: number,
  config: ThemeConfig,
  seed: number
): DecorationPlacement[] {
  const rng = mulberry32(seed);
  const result: DecorationPlacement[] = [];

  // 1. 通路に面した壁タイルを収集（たいまつ専用）
  const candidateWalls = findWallsAdjacentToFloor(tiles, mapSize);
  const shuffledForTorch = seededShuffle(candidateWalls, rng);

  // 2. たいまつ配置（通路沿いの壁のみ）
  const { targetCount, minDistance } = config.decorations.torch;
  const torchPositions: { x: number; y: number }[] = [];

  for (const wall of shuffledForTorch) {
    if (torchPositions.length >= targetCount) break;
    const tooClose = torchPositions.some(
      (t) => Math.abs(t.x - wall.x) + Math.abs(t.y - wall.y) < minDistance
    );
    if (!tooClose) {
      torchPositions.push(wall);
      result.push({ type: "torch", x: wall.x, y: wall.y });
    }
  }

  // 3. 結晶配置: 全壁タイルからたいまつタイルを除いた候補に配置
  const torchSet = new Set(torchPositions.map((t) => `${t.x},${t.y}`));
  const allWalls = seededShuffle(findAllWalls(tiles, mapSize), rng);
  const wallsForCrystals = allWalls.filter(
    (w) => !torchSet.has(`${w.x},${w.y}`)
  );

  const crystalCount = Math.min(
    config.decorations.crystal.targetCount,
    wallsForCrystals.length
  );

  for (let i = 0; i < crystalCount; i++) {
    const wall = wallsForCrystals[i];
    const gemCount = 1 + Math.floor(rng() * 2); // 1〜2個
    const gems: CrystalGem[] = [];
    for (let g = 0; g < gemCount; g++) {
      gems.push({
        top: 0.1 + rng() * 0.5, // タイル内の上部寄り
        left: 0.1 + rng() * 0.6,
        width: 4 + Math.floor(rng() * 3),
        height: 6 + Math.floor(rng() * 4),
        rotation: -20 + Math.floor(rng() * 40),
      });
    }
    result.push({ type: "crystal", x: wall.x, y: wall.y, gems });
  }

  return result;
}

/**
 * たいまつ周辺の床タイルのライティングレベルを計算
 *
 * @returns Map<"x,y", LightLevel> — "center"はたいまつ直下、"lit"は周辺
 */
export function computeLitFloors(
  decorations: readonly DecorationPlacement[],
  lightRadius: number,
  tiles: readonly (readonly TileType[])[],
  mapSize: number
): Map<string, LightLevel> {
  const litMap = new Map<string, LightLevel>();

  const torches = decorations.filter((d) => d.type === "torch");

  for (const torch of torches) {
    for (let dy = -lightRadius; dy <= lightRadius; dy++) {
      for (let dx = -lightRadius; dx <= lightRadius; dx++) {
        const nx = torch.x + dx;
        const ny = torch.y + dy;

        // 範囲外チェック
        if (nx < 0 || nx >= mapSize || ny < 0 || ny >= mapSize) continue;
        // 床タイルのみ
        if (tiles[ny][nx] !== "floor") continue;

        const dist = Math.abs(dx) + Math.abs(dy);
        const level: LightLevel = dist <= 1 ? "center" : "lit";

        const key = `${nx},${ny}`;
        // centerはlitより優先
        if (!litMap.has(key) || level === "center") {
          litMap.set(key, level);
        }
      }
    }
  }

  return litMap;
}
